import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {generatePath, useParams} from 'react-router-dom';
import * as yup from 'yup';

import {fetchPagedCustomsList} from 'api/profile/customs';
import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {editUserData$, updateCustomsUserFx} from 'models/customs/users/table';
import {CreateCustomsUser, CustomsRow, UpdateCustomsUser} from 'types/profile';

import {CustomsOption} from '../helpers/Option';

import {Actions, BackButton, CustomTitle, Field, FormFields, SubmitButton, Wrapper} from './styled';

type Fields = keyof CreateCustomsUser['registrationRequest'] | 'customsIdList' | 'password_confirm';

type FormField = {
    name: Fields;
    readonly?: boolean;
    options?: SelectOption<string>[];
};

export const UpdateCustomsUserPage = () => {
    const {t, i18n} = useTranslation();
    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language],
    );
    const translateForm = useCallback(
        (key: string) => t('pages.customsUsers.create.fields.' + key),
        [i18n.language],
    );

    const editUserData = useStore(editUserData$);
    useEffect(() => {
        if (!editUserData) return history.push(ROUTE.CUSTOMS_USERS_LIST);

        setValue('first_name', editUserData.firstName);
        setValue('middle_name', editUserData.middleName ?? '');
        setValue('last_name', editUserData.lastName);
        setValue('phone', editUserData.phone);
        setValue(
            'customsIdList',
            editUserData.customs.map((c) => c.id),
        );
    }, []);

    const params = useParams<{id: string}>();

    const schema = useMemo(
        () =>
            yup.object().shape({
                first_name: yup.string().required(translateValidates('required')),
                middle_name: yup.string().notRequired(),
                last_name: yup.string().required(translateValidates('required')),
                phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .required(translateValidates('required'))
                    .typeError(translateValidates('required'))
                    .matches(/^\+?\d{10,15}$/, translateValidates('phone')),
                customsIdList: yup
                    .array()
                    .typeError(translateValidates('required'))
                    .of(yup.number().required(translateValidates('required')))
                    .min(1, translateValidates('required')),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
        reset,
    } = useForm<Record<string, any>>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const onSubmit = async (
        data: Partial<CreateCustomsUser['registrationRequest']> & {customsIdList: number[]},
    ) => {
        if (!editUserData?.userId) return;
        try {
            const payload: UpdateCustomsUser & {id: string | number} = {
                id: String(editUserData.userId),
                firstName: {value: data.first_name || '', reset: false},
                middleName: {value: data.middle_name || '', reset: false},
                lastName: {value: data.last_name || '', reset: false},
                phone: {value: data.phone || '', reset: false},
                customsIdList: data.customsIdList,
            };

            await updateCustomsUserFx(payload);
            reset();
            if (params.id) history.push(generatePath(ROUTE.CUSTOMS_USERS, {id: params.id}));
            else history.push(generatePath(ROUTE.CUSTOMS_USERS_LIST));
        } catch (error) {
            console.log(error);
        }
    };

    const formFields: FormField[] = useMemo(
        () => [
            {name: 'first_name'},
            {name: 'middle_name'},
            {name: 'last_name'},
            {name: 'phone'},
            {name: 'customsIdList'},
        ],
        [t],
    );

    const [customs, setCustoms] = useState<CustomsRow[]>([]);
    const getCustoms = async () => {
        try {
            const response = (
                await fetchPagedCustomsList({
                    sortings: [],
                    filters: [],
                    paging: {pageSize: 50, pageNumber: 0},
                })
            ).data;
            setCustoms(response);
        } catch (error) {
            console.error(error);
        }
    };
    useEffect(() => {
        getCustoms();
    }, []);
    const customsOptions = useMemo<SelectOption<number | null>[]>(() => {
        try {
            return customs.map((item) => ({
                value: item.id ?? null,
                label: item.name,
                raw: item,
            }));
        } catch (error) {
            console.error(error);
            return [{value: null, label: ''}];
        }
    }, [customs]);

    const controlRender = (
        controlEvents: ControllerRenderProps<Record<string, any>>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
        };

        if (field.name === 'customsIdList') {
            const selectedOptions = customsOptions.filter((o) =>
                controlEvents.value.includes(o.value),
            );

            return (
                <div style={{paddingBottom: '6px'}}>
                    <Select
                        menuPlacement="top"
                        isMulti
                        value={selectedOptions}
                        options={customsOptions}
                        {...baseProps}
                        onChange={(selected: SelectOption<number>[]) => {
                            const values = selected.map((o) => o.value);
                            controlEvents.onChange(values);
                        }}
                        optionComponent={CustomsOption}
                    />
                </div>
            );
        }

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    const backPath = params.id
        ? generatePath(ROUTE.CUSTOMS_USERS, {id: params.id})
        : ROUTE.CUSTOMS_USERS_LIST;

    return (
        <>
            <BackTo to={backPath}>{t('pages.profile.staff.create.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form onSubmit={handleSubmit(onSubmit, console.error)} style={{marginTop: 50}}>
                        <CustomTitle size={4}>{t('pages.profile.staff.update.title')}</CustomTitle>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>
                                        {t(`pages.customsUsers.create.fields.${field.name}`)}
                                    </span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <Actions>
                            <BackButton
                                type="button"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(backPath);
                                    reset();
                                }}
                            >
                                {t('pages.profile.staff.create.cancel')}
                            </BackButton>
                            <SubmitButton type="submit">
                                {t('pages.profile.staff.update.updateBtn')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
        </>
    );
};
